import React, { useContext, useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  Switch,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { AuthContext } from '../context/AuthContext';
import { ClientContext } from '../context/ClientContext';

const ProfileScreen = ({ navigation }) => {
  const { userData, logout, isLoading } = useContext(AuthContext);
  const { selectedClient } = useContext(ClientContext);
  
  const [pushNotifications, setPushNotifications] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  
  const togglePushNotifications = () => setPushNotifications(prev => !prev);
  const toggleEmailNotifications = () => setEmailNotifications(prev => !prev);
  const toggleDarkMode = () => {
    Alert.alert(
      'Feature Coming Soon',
      'Dark mode will be available in an upcoming update.'
    );
  };

const handleLogout = async () => {
  //console.log('ProfileScreen: User initiated logout');

  const confirm = window.confirm('Are you sure you want to logout?');
  if (confirm) {
    //console.log('ProfileScreen: User confirmed logout');
    await logout();
  }
};

  const handleClientSelect = () => {
    navigation.navigate('ClientSelection');
  };

  const handleChangePassword = () => {
    navigation.navigate('ResetPassword', { email: userData?.username });
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#3498db" />
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView>
        <View style={styles.header}>
          <View style={styles.avatarContainer}>
            <Text style={styles.avatarText}>
              {userData?.name ? userData.name.charAt(0).toUpperCase() : 'U'}
            </Text>
          </View>
          <Text style={styles.userName}>{userData?.name || 'User'}</Text>
          <Text style={styles.userEmail}>{userData?.username || 'user@example.com'}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Client</Text>
          <TouchableOpacity
            style={styles.menuItem}
            onPress={handleClientSelect}
          >
            <View style={styles.menuItemLeft}>
              <Ionicons name="business-outline" size={22} color="#3498db" style={styles.menuIcon} />
              <Text style={styles.menuLabel}>Selected Client</Text>
            </View>
            <View style={styles.menuItemRight}>
              <Text style={styles.menuValue}>
                {selectedClient ? selectedClient.clientName : 'None selected'}
              </Text>
              <Ionicons name="chevron-forward" size={16} color="#95a5a6" />
            </View>
          </TouchableOpacity>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Account</Text>
          <TouchableOpacity
            style={styles.menuItem}
            onPress={handleChangePassword}
          >
            <View style={styles.menuItemLeft}>
              <Ionicons name="key-outline" size={22} color="#e67e22" style={styles.menuIcon} />
              <Text style={styles.menuLabel}>Change Password</Text>
            </View>
            <Ionicons name="chevron-forward" size={16} color="#95a5a6" />
          </TouchableOpacity>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Notifications</Text>
          <View style={styles.menuItem}>
            <View style={styles.menuItemLeft}>
              <Ionicons name="notifications-outline" size={22} color="#9b59b6" style={styles.menuIcon} />
              <Text style={styles.menuLabel}>Push Notifications</Text>
            </View>
            <Switch
              value={pushNotifications}
              onValueChange={togglePushNotifications}
              trackColor={{ false: '#bdc3c7', true: '#3498db' }}
              thumbColor="#fff"
            />
          </View>
          <View style={styles.menuItem}>
            <View style={styles.menuItemLeft}>
              <Ionicons name="mail-outline" size={22} color="#2ecc71" style={styles.menuIcon} />
              <Text style={styles.menuLabel}>Email Notifications</Text>
            </View>
            <Switch
              value={emailNotifications}
              onValueChange={toggleEmailNotifications}
              trackColor={{ false: '#bdc3c7', true: '#3498db' }}
              thumbColor="#fff"
            />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Display</Text>
          <View style={styles.menuItem}>
            <View style={styles.menuItemLeft}>
              <Ionicons name="moon-outline" size={22} color="#34495e" style={styles.menuIcon} />
              <Text style={styles.menuLabel}>Dark Mode</Text>
            </View>
            <Switch
              value={darkMode}
              onValueChange={toggleDarkMode}
              trackColor={{ false: '#bdc3c7', true: '#3498db' }}
              thumbColor="#fff"
              disabled={true}
            />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>About</Text>
          <View style={styles.menuItem}>
            <View style={styles.menuItemLeft}>
              <Ionicons name="information-circle-outline" size={22} color="#f39c12" style={styles.menuIcon} />
              <Text style={styles.menuLabel}>Version</Text>
            </View>
            <Text style={styles.menuValue}>1.0.0</Text>
          </View>
        </View>

        <TouchableOpacity
          style={styles.logoutButton}
          onPress={handleLogout}
        >
          <Ionicons name="log-out-outline" size={20} color="#fff" style={styles.logoutIcon} />
          <Text style={styles.logoutText}>Logout</Text>
        </TouchableOpacity>

        <Text style={styles.footer}>
          &copy; {new Date().getFullYear()} POS Sales Reports
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    alignItems: 'center',
    padding: 20,
    paddingTop: 30,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  avatarContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#3498db',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 10,
  },
  avatarText: {
    fontSize: 32,
    color: '#fff',
    fontWeight: 'bold',
  },
  userName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 5,
  },
  userEmail: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  section: {
    backgroundColor: '#fff',
    marginVertical: 10,
    paddingHorizontal: 15,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#e0e0e0',
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: '500',
    color: '#7f8c8d',
    textTransform: 'uppercase',
    letterSpacing: 1,
    paddingVertical: 10,
  },
  menuItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 15,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  menuItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  menuIcon: {
    marginRight: 12,
  },
  menuLabel: {
    fontSize: 16,
    color: '#2c3e50',
  },
  menuItemRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  menuValue: {
    fontSize: 14,
    color: '#95a5a6',
    marginRight: 10,
  },
  logoutButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#e74c3c',
    marginHorizontal: 20,
    marginVertical: 20,
    padding: 12,
    borderRadius: 8,
  },
  logoutIcon: {
    marginRight: 8,
  },
  logoutText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '500',
  },
  footer: {
    textAlign: 'center',
    marginBottom: 30,
    fontSize: 12,
    color: '#95a5a6',
  },
});

export default ProfileScreen;